import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Gauge, PackageX, BadgePercent, HardDrive, Layers, BarChart3,
  Eye, Radar, TrendingUp, Lightbulb, FileText, MoveDown,
} from "lucide-react";
import { ComposedChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from "recharts";
import { useLang } from "@/i18n";
import { Reveal, FadeStagger, FadeItem } from "@/components/Motion";

const CAP_ICONS = [Gauge, PackageX, BadgePercent, HardDrive, Layers, BarChart3];
const CTRL_ICONS = [Eye, Radar, TrendingUp, Lightbulb, FileText];

const CHART_DATA = [
  { m: 1, usage: 100, bulk: 118, adaptive: 98 },
  { m: 2, usage: 94, bulk: 118, adaptive: 94 },
  { m: 3, usage: 108, bulk: 118, adaptive: 100 },
  { m: 4, usage: 122, bulk: 118, adaptive: 106 },
  { m: 5, usage: 100, bulk: 118, adaptive: 100 },
  { m: 6, usage: 86, bulk: 118, adaptive: 90 },
  { m: 7, usage: 92, bulk: 118, adaptive: 92 },
  { m: 8, usage: 112, bulk: 118, adaptive: 100 },
  { m: 9, usage: 126, bulk: 118, adaptive: 110 },
  { m: 10, usage: 118, bulk: 118, adaptive: 108 },
  { m: 11, usage: 102, bulk: 118, adaptive: 100 },
  { m: 12, usage: 96, bulk: 118, adaptive: 96 },
];

const CostOptSections = () => {
  const { t } = useLang();
  const c = t.aws.costopt;

  return (
    <>
      {/* Intro */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] grid gap-10 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{c.introTitle}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base font-jakarta text-[#353535]/85 leading-relaxed">{c.introBody1}</p>
            <p className="mt-5 text-sm sm:text-base font-jakarta text-[#999999] leading-relaxed">{c.introBody2}</p>
            <Link data-testid="costopt-hero-cta" to="/contact?unit=aws&service=aws-cost-optimization" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#4361ee] px-7 py-3.5 text-sm font-jakarta font-bold text-white transition-all duration-300 hover:gap-4">
              {c.ctaSavings} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="px-5 sm:px-8 py-16 sm:py-20 border-t border-black/10">
        <FadeStagger className="mx-auto max-w-[1400px] grid gap-5 sm:grid-cols-2 lg:grid-cols-3" delay={0.05}>
          {c.capabilities.map((cap, i) => {
            const Icon = CAP_ICONS[i];
            return (
              <FadeItem key={i}>
                <div data-testid={`costopt-cap-${i}`} className="h-full rounded-2xl border border-black/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(67,97,238,0.10)]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#4361ee]/10 text-[#4361ee]">
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-5 font-cabinet text-lg font-bold">{cap.t}</h3>
                  <p className="mt-2 text-sm text-[#999999] font-jakarta leading-relaxed">{cap.d}</p>
                </div>
              </FadeItem>
            );
          })}
        </FadeStagger>
      </section>

      {/* 5-step plan */}
      <section className="bg-[#353535] text-white px-5 sm:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight max-w-3xl">{c.stepsKicker}</h2>
          </Reveal>
          <div className="mt-14 flex flex-col gap-0">
            {c.steps.map((s, i) => (
              <Reveal key={i} className="grid gap-4 lg:grid-cols-[120px_1fr_1.4fr] border-t border-white/10 py-9 items-start">
                <span className="font-cabinet text-3xl font-bold text-[#ff764a]">{s.n}</span>
                <h3 className="font-cabinet text-xl sm:text-2xl font-bold">{s.t}</h3>
                <div>
                  <p className="text-sm sm:text-base font-jakarta text-white/70 leading-relaxed">{s.d}</p>
                  {s.chips && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {s.chips.map((chip, j) => (
                        <span key={j} className="rounded-full border border-white/15 px-3.5 py-1.5 font-jbmono text-[10px] uppercase tracking-widest text-white/70">{chip}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous optimization + chart */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-10 lg:grid-cols-2 items-start">
            <Reveal>
              <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#ff764a] mb-4">{c.contKicker}</div>
              <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{c.contTitle}</h2>
              <p className="mt-6 text-sm sm:text-base font-jakarta text-[#999999] leading-relaxed">{c.contBody}</p>
            </Reveal>
            <FadeStagger className="flex flex-col gap-4" delay={0.1}>
              {c.contCards.map((card, i) => (
                <FadeItem key={i} className="rounded-2xl border border-black/10 bg-white p-6 flex items-start gap-4">
                  <span className="font-jbmono text-xs text-[#4361ee] shrink-0 mt-1">0{i + 1}</span>
                  <div>
                    <div className="font-cabinet text-base font-bold">{card.t}</div>
                    <p className="mt-1 text-sm font-jakarta text-[#999999] leading-relaxed">{card.d}</p>
                  </div>
                </FadeItem>
              ))}
            </FadeStagger>
          </div>

          <Reveal className="mt-14">
            <div data-testid="costopt-comparison-chart" className="rounded-3xl border border-black/10 bg-white p-6 sm:p-8">
              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
                <span className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-widest text-[#999999]"><span className="h-[3px] w-6 rounded-full bg-[#999999]" /> {c.chartUsage}</span>
                <span className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-widest text-[#999999]"><span className="h-[3px] w-6 rounded-full bg-[#ca3b42]" /> {c.chartBulk}</span>
                <span className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-widest text-[#999999]"><span className="h-[3px] w-6 rounded-full bg-[#0d9488]" /> {c.chartAdaptive}</span>
              </div>
              <div className="h-72 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={CHART_DATA} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="rgba(0,0,0,0.07)" vertical={false} />
                    <XAxis dataKey="m" ticks={[1, 6, 12]} tick={{ fontSize: 11, fill: "#999999", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(0,0,0,0.1)" }} tickLine={false} />
                    <YAxis hide domain={[60, 135]} />
                    <Line type="monotone" dataKey="usage" stroke="#999999" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                    <Line type="monotone" dataKey="bulk" stroke="#ca3b42" strokeWidth={2.5} dot={false} />
                    <Line type="stepAfter" dataKey="adaptive" stroke="#0d9488" strokeWidth={2.5} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm font-jakarta text-[#999999] max-w-2xl">{c.contNote}</p>
            <div data-testid="costopt-cont-result" className="mt-5 rounded-2xl border border-[#4361ee]/20 bg-[#4361ee]/[0.07] px-7 py-5 font-cabinet text-base sm:text-lg font-bold text-[#353535]">
              {c.contResult}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Control */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight max-w-2xl">{c.controlTitle}</h2>
            <p className="mt-4 text-sm sm:text-base font-jakarta text-[#999999] max-w-xl">{c.controlSub}</p>
          </Reveal>
          <FadeStagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" delay={0.05}>
            {c.controlItems.map((item, i) => {
              const Icon = CTRL_ICONS[i];
              return (
                <FadeItem key={i}>
                  <div className="h-full rounded-2xl border border-black/10 bg-white p-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#4361ee]/10 text-[#4361ee]">
                      <Icon size={18} />
                    </span>
                    <div className="mt-4 font-cabinet text-sm font-bold leading-snug">{item.t}</div>
                    <p className="mt-2 text-[13px] font-jakarta text-[#999999] leading-relaxed">{item.d}</p>
                  </div>
                </FadeItem>
              );
            })}
          </FadeStagger>
        </div>
      </section>

      {/* FinOps cycle */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#ff764a] mb-4">{c.cycleKicker}</div>
          </Reveal>
          <FadeStagger className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" delay={0.05}>
            {c.cycle.map((step, i) => (
              <FadeItem key={i} className="relative">
                <div data-testid={`costopt-cycle-${i}`} className="h-full rounded-2xl border border-black/10 bg-white p-7">
                  <div className="font-jbmono text-xs text-[#4361ee]">{step.n}</div>
                  <div className="mt-2 font-cabinet text-xl font-bold tracking-tight">{step.t}</div>
                  <p className="mt-2 text-sm font-jakarta text-[#999999] leading-relaxed">{step.d}</p>
                </div>
                {i < c.cycle.length - 1 && (
                  <MoveDown size={16} className="hidden lg:block absolute -right-[13px] top-1/2 -translate-y-1/2 -rotate-90 text-[#999999] z-10 bg-[#FAFAFD] rounded-full" />
                )}
              </FadeItem>
            ))}
          </FadeStagger>
          <Reveal delay={0.15}>
            <div className="mt-8 font-cabinet text-lg sm:text-xl font-bold text-[#4361ee]">{c.cycleTags}</div>
            <p className="mt-2 text-sm font-jakarta text-[#999999] max-w-2xl">{c.cycleNote}</p>
          </Reveal>
        </div>
      </section>

      {/* BIG commercial block */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <Reveal className="mx-auto max-w-[1400px] rounded-3xl bg-gradient-to-br from-[#4361ee] to-[#3050d8] text-white p-8 sm:p-16 overflow-hidden relative">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-white/10" />
          <div className="relative">
            <h2 className="font-playfair leading-[1.05] text-[8vw] sm:text-[4.5vw] lg:text-[3.6vw] tracking-tight">
              {c.bigTitle1}
              <span className="block text-[#ff764a]">{c.bigTitle2}</span>
            </h2>
            <p className="mt-6 text-white/80 font-jakarta max-w-2xl text-sm sm:text-base leading-relaxed">{c.bigSub}</p>
            <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {c.bigChain.map((link, i) => (
                <div key={i} data-testid={`costopt-chain-${i}`} className="relative rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-6">
                  <div className="font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#ff764a]">{link.t}</div>
                  <div className="mt-2 font-cabinet text-lg font-bold">{link.s}</div>
                  <p className="mt-2 text-[13px] font-jakarta text-white/70 leading-relaxed">{link.d}</p>
                  {i < c.bigChain.length - 1 && (
                    <ArrowRight size={16} className="hidden lg:block absolute -right-[13px] top-1/2 -translate-y-1/2 text-white/60 z-10" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link data-testid="costopt-big-cta" to="/contact?unit=aws&service=aws-cost-optimization" className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-jakarta font-bold text-[#4361ee] transition-all duration-300 hover:gap-4">
                {c.bigCta} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <span className="font-jbmono text-[10px] uppercase tracking-widest text-white/50">{c.bigFootnote}</span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Initial assessment */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{c.auditTitle}</h2>
            <p className="mt-5 text-sm sm:text-base font-jakarta text-[#999999] max-w-md leading-relaxed">{c.auditSub}</p>
            <p className="mt-6 text-sm font-jakarta font-semibold text-[#353535]/85 max-w-md leading-relaxed">{c.auditNote}</p>
            <Link data-testid="costopt-audit-cta" to="/contact?unit=aws&service=aws-cost-optimization" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#4361ee] px-8 py-4 text-sm font-jakarta font-bold text-white transition-all duration-300 hover:gap-4">
              {c.auditCta} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-black/10 bg-white p-7 sm:p-9 shadow-[0_24px_70px_rgba(30,30,56,0.08)]">
              <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-6">{c.auditLabel}</div>
              <div className="flex flex-col gap-3.5">
                {c.auditItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0d9488]/10">
                      <Check size={12} className="text-[#0d9488]" />
                    </span>
                    <span className="text-sm font-jakarta text-[#353535]/85">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* A4P + AWS expertise */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#ff764a] mb-4">{c.whyKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight max-w-3xl">{c.whyTitle}</h2>
            <p className="mt-6 text-sm sm:text-base font-jakarta text-[#999999] max-w-2xl leading-relaxed">{c.whyBody}</p>
          </Reveal>
          <FadeStagger className="mt-12 flex flex-wrap gap-3" delay={0.05}>
            {c.whyChips.map((chip, i) => (
              <FadeItem key={i}>
                <div className="rounded-2xl border border-black/10 bg-white px-6 py-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#4361ee]/40">
                  <div className="font-cabinet text-base font-bold">{chip.t}</div>
                  <p className="mt-1 text-[13px] font-jakarta text-[#999999]">{chip.d}</p>
                </div>
              </FadeItem>
            ))}
          </FadeStagger>
        </div>
      </section>

      {/* SAVE / OPTIMIZE / BUILD positioning */}
      <section className="bg-[#353535] text-white px-5 sm:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <FadeStagger className="grid gap-px bg-white/10 rounded-2xl overflow-hidden lg:grid-cols-3" delay={0.05}>
            {[c.posSave, c.posOptimize, c.posBuild].map((pos, i) => (
              <FadeItem key={i} className="bg-[#353535] p-9" data-testid={`costopt-pos-${i}`}>
                <div className={`font-cabinet text-2xl font-black tracking-tight ${["text-[#ff764a]", "text-[#4361ee]", "text-[#0d9488]"][i]}`}>{pos.t}</div>
                <div className="mt-3 font-cabinet text-lg font-bold">{pos.d}</div>
                <p className="mt-2 text-sm font-jakarta text-white/60">{pos.s}</p>
              </FadeItem>
            ))}
          </FadeStagger>
          <Reveal delay={0.2}>
            <p className="mt-14 font-playfair text-2xl sm:text-4xl leading-snug max-w-3xl">{c.posMsg}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default CostOptSections;
